/**
 * GuardianMark — signature brand treatment for the Guardian Program™ family.
 *
 * One component, three variants, unmistakable everywhere:
 *
 *   <GuardianMark />                          → Guardian Program™ (default)
 *   <GuardianMark term="Guardian Assessment"/> → for the entry point
 *   <GuardianMark term="Guardian Report" />    → for the deliverable
 *   <GuardianMark term="Guardian Stewardship"/> → for the ongoing service
 *   <GuardianMark term="Guardian History" />   → for the long-term record
 *
 *   variant="signature"  → adds the shield glyph + tighter tracking
 *   variant="inline"     → default, inline body treatment
 *   tone="light"|"navy"|"gold"  → adapts the ™ + shield contrast per background
 *
 * Rules (Apple-style product typography):
 *  • Always the SRM display font — never swap.
 *  • Slightly heavier weight, negative tracking on the wordmark.
 *  • Gold ™ superscript at a proportional 0.42em, lifted, whitespace-nowrap.
 *  • Optional shield motif — 12px stroke, gold, only in "signature" contexts.
 *  • Never loud, never gimmicky.
 */

function ShieldGlyph({ tone = "gold", className = "" }) {
    const stroke =
        tone === "light" ? "#EAD9AE" : tone === "navy" ? "#0F2856" : "#C9A55C";
    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 24 28"
            className={`inline-block ${className}`}
            style={{ verticalAlign: "-0.14em" }}
        >
            <path
                d="M12 1.5 L2.5 5.4 V14.4 C2.5 20 6.6 24.8 12 26.5 C17.4 24.8 21.5 20 21.5 14.4 V5.4 L12 1.5 Z"
                fill="none"
                stroke={stroke}
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
            <path
                d="M7.5 13.2 L10.7 16.5 L16.5 10.5"
                fill="none"
                stroke={stroke}
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default function GuardianMark({
    term = "Guardian Program",
    tone = "gold",
    variant = "inline",
    className = "",
}) {
    const tmClass =
        tone === "light"
            ? "text-white/70"
            : tone === "navy"
              ? "text-srm-navy/70"
              : "text-srm-gold";

    // Signature = the premium wordmark treatment used in hero H1s,
    // section titles and any "moment" copy. Adds the shield, tightens
    // the letter-spacing slightly, and nudges the weight up a hair.
    if (variant === "signature") {
        return (
            <span
                className={`whitespace-nowrap font-semibold ${className}`}
                style={{ letterSpacing: "-0.012em" }}
            >
                <ShieldGlyph
                    tone={tone}
                    className="mr-[0.35em] h-[0.9em] w-[0.77em]"
                />
                {term}
                <sup
                    className={`text-[0.42em] font-semibold tracking-normal ${tmClass}`}
                    style={{ verticalAlign: "0.55em", marginLeft: "0.06em" }}
                >
                    ™
                </sup>
            </span>
        );
    }

    // Inline = default. Cheap and consistent for body text, buttons,
    // list items, testimonials. No shield here — that would be noisy.
    return (
        <span
            className={`whitespace-nowrap font-semibold ${className}`}
            style={{ letterSpacing: "-0.005em" }}
        >
            {term}
            <sup
                className={`text-[0.42em] font-semibold tracking-normal ${tmClass}`}
                style={{ verticalAlign: "0.55em", marginLeft: "0.06em" }}
            >
                ™
            </sup>
        </span>
    );
}

/* Named export so the standalone glyph can be used sparingly for
   badges, eyebrows and section decorations without printing the
   wordmark next to it. */
export { ShieldGlyph };
